/**
 * Non-disclosure agreement types.
 *
 * The app is in private testing and the server refuses every authenticated request from an
 * account that has not accepted the agreement in force, so these shapes back a gate rather than
 * a preference.
 */

/**
 * What `GET /api/nda/current` answers.
 *
 * Two shapes come back from one endpoint. When nothing has been published the server says
 * `{ required: false, accepted: true, version: null }` — a valid state, and the honest answer to
 * "is there anything to sign", so the fields describing a document are simply absent. When a
 * version is in force every field is present.
 */
export interface NdaStatus {
  /** Whether an agreement has been published at all. */
  required: boolean
  /** Whether this user has accepted the version in force. True when nothing is published. */
  accepted: boolean
  /** The version in force, or null when nothing is published. */
  version: number | null
  title?: string
  /** The agreement itself, as authored. Plain text. */
  body?: string
  publishedAt?: string
}

/** What `POST /api/nda/accept` answers on success. */
export interface NdaAcceptResponse {
  accepted: true
  version: number
  acceptedAt: string
}

/**
 * The 409 body when the text changed between loading it and pressing accept.
 *
 * The server refuses rather than recording a signature against words nobody read. `version` is
 * the one now in force.
 */
export interface NdaStaleVersionError {
  error: 'nda_version_stale'
  message: string
  version: number | null
}

/** The 403 body the gate middleware returns on every request from an account that has not signed. */
export interface NdaRequiredError {
  error: 'nda_required'
  message: string
  version: number | null
}

export const NDA_REQUIRED_CODE = 'nda_required'
export const NDA_VERSION_STALE_CODE = 'nda_version_stale'
